import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router-dom';

import ArticleIcon from '@mui/icons-material/Article';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import CheckIcon from '@mui/icons-material/Check';
import EmailIcon from '@mui/icons-material/Email';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import FinePrintIcon from '@mui/icons-material/ManageSearch';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SubjectIcon from '@mui/icons-material/Subject';
import SyncIcon from '@mui/icons-material/Sync';
import TuneIcon from '@mui/icons-material/Tune';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import AIGeneratorButton from '~/components/AIGenerator/AIGenerateButton';
import OfferPageHighlightTagsOverride from '~/components/Common/Forms/OfferForm/OfferPageHighlightTagsOverride';
import TransferDetailsFields from '~/components/Common/Forms/OfferForm/TransferDetailsFields';
import {
  LUX_PLUS_FINEPRINT_CONTENT_FLASH,
  LUX_PLUS_FINEPRINT_CONTENT_LPC,
} from '~/components/Common/Forms/OfferForm/constants/fineprint';
import {
  areTransferDetailsEqual,
  serializeTransferDetails,
  toTransferDetailsFormValue,
} from '~/components/Common/Forms/OfferForm/transferDetails';
import { OfferInclusionsSelect, RichTextEditor } from '~/components/Common/Forms/fields';
import GenerateContentModal from '~/components/Common/GenerateContentModal';

import { OFFER_FACING_NAMES } from '~/consts/offerTypes';

import { useGetOfferContent, useInitiateOfferContentGeneration } from '~/hooks/apis/accommodation/useOfferContent';
import { useSyncPropertyFacilities } from '~/hooks/apis/accommodation/useSyncPropertyFacilities';
import { useGetOfferSchema, useUpdateOffer } from '~/hooks/apis/offers/useGetOffer';
import { useGetOfferTemplates } from '~/hooks/apis/offers/useGetOfferTemplates';

import InclusionsService from '~/services/InclusionsService';

import OfferGeneratedContentView from '../../../../Edit/OfferGeneratedContentView';
import OfferTemplateModal from '../../../../Edit/OfferTemplateModal';
import ContentActionBar, { CONTENT_ACTION_BUTTON_SX } from '../ContentActionBar';
import OfferPagePreview from '../OfferPagePreview';

import type { GeneratedContentFields, OfferSaveAction, Props, SummaryBodyFormValues } from './types';
import {
  getApprovedSaveStatus,
  isValidSlug,
  normalizeRichTextValue,
  resolveOfferLocationValue,
  showSaveError,
} from './utils';

const AI_BUTTON_SX = {
  m: 0,
  maxWidth: 'none',
  p: 0,
  '&.MuiContainer-root': {
    px: 0,
  },
  '& .MuiGridLegacy-container': {
    m: 0,
    width: 'auto',
  },
  '& .MuiGrid-container': {
    m: 0,
    width: 'auto',
  },
  '& .MuiGridLegacy-container > .MuiGridLegacy-item': {
    p: 0,
  },
  '& .MuiGrid-container > .MuiGrid-item': {
    p: 0,
  },
  '& .MuiGridLegacy-item': {
    p: 0,
  },
  '& .MuiGrid-item': {
    p: 0,
  },
};
const ACCORDION_SX = {
  bgcolor: 'background.paper',
  borderRadius: 1,
  boxShadow: 1,
  overflow: 'hidden',
  '&:before': {
    display: 'none',
  },
};
const ACCORDION_SUMMARY_SX = {
  minHeight: 64,
  px: 3,
  '& .MuiAccordionSummary-content': {
    my: 2,
  },
};
const ACCORDION_DETAILS_SX = {
  bgcolor: 'grey.50',
  borderTop: 1,
  borderColor: 'divider',
  p: 3,
};
const FINE_PRINT_TEMPLATE_TYPE = 'fine_print';
const DESCRIPTION_CONTENT_TYPE = 'description';

type SavedContentSnapshot = SummaryBodyFormValues & {
  flight_destination_port: string;
  flights_enabled: boolean;
  location: string;
  name: string;
  offer_tile_inclusion_heading: string;
  preheader: string;
  sale_unit: string;
  slug: string;
  status: string;
  subject_line: string;
  type: string;
};

function useSyncedString(value?: string, skipSync = false) {
  const [syncedValue, setSyncedValue] = useState(value ?? '');

  useEffect(() => {
    if (skipSync) {
      return;
    }

    setSyncedValue(value ?? '');
  }, [skipSync, value]);

  return [syncedValue, setSyncedValue] as const;
}

export default function DefaultContent({
  offerAdditionalDescription,
  offerDescription,
  offerFacilities,
  offerFinePrint,
  offerTransferDetails,
  offerFlightDestinationPort,
  offerFlightsEnabled,
  offerHighlights,
  offerInclusions,
  offerLocation,
  offerPageHighlightTagsOverride,
  offerName,
  offerPreheader,
  offerSaleUnit,
  offerSlug,
  offerStatus,
  offerSubjectLine,
  offerTileInclusionHeading,
  offerType,
  copiedOfferContent,
  packages,
  propertyId,
  propertyName,
  vendorId,
  onOfferStatusSaved,
}: Props) {
  const { id_offer: offerId } = useParams<{ id_offer: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const [isTemplateModalVisible, setIsTemplateModalVisible] = useState(false);
  const [isGeneralSettingsExpanded, setIsGeneralSettingsExpanded] = useState(true);
  const [isOfferPageExpanded, setIsOfferPageExpanded] = useState(true);
  const [isMerchandisingEmailExpanded, setIsMerchandisingEmailExpanded] = useState(true);
  const [isInclusionsExpanded, setIsInclusionsExpanded] = useState(true);
  const [isFlightsExpanded, setIsFlightsExpanded] = useState(true);
  const [isSummaryBodyExpanded, setIsSummaryBodyExpanded] = useState(true);
  const [finePrintMenuAnchorEl, setFinePrintMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [isGeneratedContentDialogOpen, setIsGeneratedContentDialogOpen] = useState(false);
  const [isGenerateContentModalVisible, setIsGenerateContentModalVisible] = useState(false);
  const [useGeneratedContentWebSearch, setUseGeneratedContentWebSearch] = useState(false);
  const hasCopiedContentDraftRef = useRef(false);
  const skipCopiedContentDraftSync = hasCopiedContentDraftRef.current;
  const [emailPreheaderValue, setEmailPreheaderValue] = useSyncedString(offerPreheader, skipCopiedContentDraftSync);
  const [emailSubjectLineValue, setEmailSubjectLineValue] = useSyncedString(
    offerSubjectLine,
    skipCopiedContentDraftSync,
  );
  const [flightDestinationPortValue, setFlightDestinationPortValue] = useSyncedString(
    offerFlightDestinationPort,
    skipCopiedContentDraftSync,
  );
  const [flightsEnabledValue, setFlightsEnabledValue] = useState(!!offerFlightsEnabled);
  const [headlineValue, setHeadlineValue] = useSyncedString(offerName, skipCopiedContentDraftSync);
  const [locationValue, setLocationValue] = useSyncedString(offerLocation, skipCopiedContentDraftSync);
  const [offerTileInclusionHeadingValue, setOfferTileInclusionHeadingValue] = useSyncedString(
    offerTileInclusionHeading,
    skipCopiedContentDraftSync,
  );
  const [saleUnitValue, setSaleUnitValue] = useSyncedString(offerSaleUnit, skipCopiedContentDraftSync);
  const [slugValue, setSlugValue] = useSyncedString(offerSlug, skipCopiedContentDraftSync);
  const [typeValue, setTypeValue] = useSyncedString(offerType, skipCopiedContentDraftSync);
  const [statusValue, setStatusValue] = useSyncedString(offerStatus, skipCopiedContentDraftSync);
  const [pendingSaveAction, setPendingSaveAction] = useState<OfferSaveAction>();
  const { data: offerTemplates = [] } = useGetOfferTemplates();
  const { data: offerSchema } = useGetOfferSchema(offerId);
  const { mutateAsync: updateOffer, isPending: isSavingOffer } = useUpdateOffer(offerId, {
    invalidateOnSuccess: false,
  });
  const offerContentSnapshot = useMemo<SavedContentSnapshot>(
    () => ({
      additional_description: offerAdditionalDescription ?? '',
      description: offerDescription ?? '',
      facilities: offerFacilities ?? '',
      fine_print: offerFinePrint ?? '',
      transfer_details: offerTransferDetails ?? null,
      flight_destination_port: offerFlightDestinationPort ?? '',
      flights_enabled: !!offerFlightsEnabled,
      highlights: offerHighlights ?? '',
      location: offerLocation ?? '',
      name: offerName ?? '',
      offer_inclusions: offerInclusions ?? [],
      offer_tile_inclusion_heading: offerTileInclusionHeading ?? '',
      preheader: offerPreheader ?? '',
      sale_unit: offerSaleUnit ?? '',
      slug: offerSlug ?? '',
      status: offerStatus ?? '',
      subject_line: offerSubjectLine ?? '',
      type: offerType ?? '',
    }),
    [
      offerAdditionalDescription,
      offerDescription,
      offerFacilities,
      offerFinePrint,
      offerTransferDetails,
      offerFlightDestinationPort,
      offerFlightsEnabled,
      offerHighlights,
      offerInclusions,
      offerLocation,
      offerName,
      offerPreheader,
      offerSaleUnit,
      offerSlug,
      offerStatus,
      offerSubjectLine,
      offerTileInclusionHeading,
      offerType,
    ],
  );
  const [savedContentSnapshot, setSavedContentSnapshot] = useState(offerContentSnapshot);
  const {
    data: offerContent,
    error: generatedContentError,
    isFetching: isGeneratedContentLoading,
    refetch: refetchGeneratedContent,
  } = useGetOfferContent({
    offerId,
    enabled: isGeneratedContentDialogOpen,
  });
  const { mutateAsync: initiateGeneratedContentGenerationMutation } = useInitiateOfferContentGeneration(offerId);
  const { control, getValues, reset, setValue, watch } = useForm<SummaryBodyFormValues>({
    defaultValues: {
      additional_description: offerAdditionalDescription ?? '',
      description: offerDescription ?? '',
      facilities: offerFacilities ?? '',
      fine_print: offerFinePrint ?? '',
      highlights: offerHighlights ?? '',
      offer_inclusions: offerInclusions ?? [],
      offer_page_highlight_tags_override: offerPageHighlightTagsOverride ?? null,
      transfer_details: toTransferDetailsFormValue(offerTransferDetails),
    },
  });
  const { mutate: syncFacilities, isPending: isSyncingFacilities } = useSyncPropertyFacilities({
    onSuccess: (facilities) => {
      setValue(
        'facilities',
        facilities.map((item) => `- ${item.name}`).join('\n') +
          '\n\nSurcharges may apply to select facilities and services',
      );
    },
  });
  const additionalDescriptionValue = watch('additional_description') ?? '';
  const descriptionValue = watch('description') ?? '';
  const highlightsValue = watch('highlights') ?? '';
  const facilitiesValue = watch('facilities') ?? '';
  const finePrintValue = watch('fine_print') ?? '';
  const transferDetailsValue = watch('transfer_details');
  const offerInclusionsValue = watch('offer_inclusions');
  const typeOptions = offerSchema?.properties?.type?.enum ?? [];
  const statusOptions = offerSchema?.properties?.status?.enum ?? [];
  const flightDestinationPortOptions = offerSchema?.properties?.flight_destination_port?.enum ?? [];
  const structuredFinePrintPath = `/edit-offers/${offerId}/structured-fine-print`;
  const structuredFacilitiesPath = `/edit-offers/${offerId}/structured-facilities`;
  const visibleInclusionCount = offerInclusionsValue.length;
  const shouldShowStatusFallback = !!statusValue && !statusOptions.some((status) => status === statusValue);
  const isSlugInvalid = slugValue.trim() !== '' && !isValidSlug(slugValue.trim());
  const generatedContent = useMemo(() => offerContent?.generatedContents ?? [], [offerContent]);
  const visibleGeneratedContent = useMemo(
    () => generatedContent.filter((field) => field.fields === DESCRIPTION_CONTENT_TYPE),
    [generatedContent],
  );
  const hasAnyGeneratedContent = generatedContent.length > 0;
  const hasVisibleGeneratedContent = visibleGeneratedContent.length > 0;
  const generatedContentErrorMessage = generatedContentError instanceof Error ? generatedContentError.message : null;
  const filteredTemplates = useMemo(
    () =>
      offerTemplates.filter((template) => {
        const templateMatchesOfferType = template.offer_type === 'all' || template.offer_type === offerType;

        return template.template_type === FINE_PRINT_TEMPLATE_TYPE && templateMatchesOfferType;
      }),
    [offerTemplates, offerType],
  );
  const headlineAiContext = useMemo(
    () => ({
      model: 'offer',
      field: 'headline',
      propertyName: headlineValue,
    }),
    [headlineValue],
  );
  const descriptionAiContext = useMemo(
    () => ({
      model: 'offer',
      field: 'description',
      propertyName: headlineValue,
    }),
    [headlineValue],
  );
  const updateHeadlineAiContext = useCallback(() => headlineAiContext, [headlineAiContext]);
  const updateDescriptionAiContext = useCallback(() => descriptionAiContext, [descriptionAiContext]);
  const hasUnsavedChanges = useMemo(
    () =>
      headlineValue !== savedContentSnapshot.name ||
      locationValue !== savedContentSnapshot.location ||
      saleUnitValue !== savedContentSnapshot.sale_unit ||
      emailSubjectLineValue !== savedContentSnapshot.subject_line ||
      emailPreheaderValue !== savedContentSnapshot.preheader ||
      flightDestinationPortValue !== savedContentSnapshot.flight_destination_port ||
      flightsEnabledValue !== savedContentSnapshot.flights_enabled ||
      offerTileInclusionHeadingValue !== savedContentSnapshot.offer_tile_inclusion_heading ||
      slugValue !== savedContentSnapshot.slug ||
      typeValue !== savedContentSnapshot.type ||
      statusValue !== savedContentSnapshot.status ||
      normalizeRichTextValue(additionalDescriptionValue) !==
        normalizeRichTextValue(savedContentSnapshot.additional_description) ||
      normalizeRichTextValue(descriptionValue) !== normalizeRichTextValue(savedContentSnapshot.description) ||
      normalizeRichTextValue(facilitiesValue) !== normalizeRichTextValue(savedContentSnapshot.facilities) ||
      normalizeRichTextValue(finePrintValue) !== normalizeRichTextValue(savedContentSnapshot.fine_print) ||
      !areTransferDetailsEqual(transferDetailsValue, savedContentSnapshot.transfer_details) ||
      normalizeRichTextValue(highlightsValue) !== normalizeRichTextValue(savedContentSnapshot.highlights) ||
      JSON.stringify(offerInclusionsValue) !== JSON.stringify(savedContentSnapshot.offer_inclusions),
    [
      additionalDescriptionValue,
      descriptionValue,
      emailPreheaderValue,
      emailSubjectLineValue,
      flightDestinationPortValue,
      flightsEnabledValue,
      facilitiesValue,
      finePrintValue,
      transferDetailsValue,
      headlineValue,
      highlightsValue,
      locationValue,
      offerInclusionsValue,
      offerTileInclusionHeadingValue,
      saleUnitValue,
      savedContentSnapshot,
      slugValue,
      statusValue,
      typeValue,
    ],
  );
  const handleDiscard = useCallback(() => {
    setEmailPreheaderValue(savedContentSnapshot.preheader);
    setEmailSubjectLineValue(savedContentSnapshot.subject_line);
    setFlightDestinationPortValue(savedContentSnapshot.flight_destination_port);
    setFlightsEnabledValue(savedContentSnapshot.flights_enabled);
    setHeadlineValue(savedContentSnapshot.name);
    setLocationValue(savedContentSnapshot.location);
    setOfferTileInclusionHeadingValue(savedContentSnapshot.offer_tile_inclusion_heading);
    setSaleUnitValue(savedContentSnapshot.sale_unit);
    setSlugValue(savedContentSnapshot.slug);
    setStatusValue(savedContentSnapshot.status);
    setTypeValue(savedContentSnapshot.type);
    reset({
      additional_description: savedContentSnapshot.additional_description,
      description: savedContentSnapshot.description,
      facilities: savedContentSnapshot.facilities,
      fine_print: savedContentSnapshot.fine_print,
      transfer_details: toTransferDetailsFormValue(savedContentSnapshot.transfer_details),
      highlights: savedContentSnapshot.highlights,
      offer_inclusions: savedContentSnapshot.offer_inclusions,
      offer_page_highlight_tags_override: getValues('offer_page_highlight_tags_override'),
    });

    hasCopiedContentDraftRef.current = false;
    enqueueSnackbar('Changes discarded', { variant: 'success' });
  }, [
    enqueueSnackbar,
    getValues,
    reset,
    savedContentSnapshot,
    setEmailPreheaderValue,
    setEmailSubjectLineValue,
    setFlightDestinationPortValue,
    setFlightsEnabledValue,
    setHeadlineValue,
    setLocationValue,
    setOfferTileInclusionHeadingValue,
    setSaleUnitValue,
    setSlugValue,
    setStatusValue,
    setTypeValue,
  ]);
  const saveOfferContent = useCallback(
    async (saveAction: OfferSaveAction) => {
      const nextStatus = saveAction === 'draft' ? 'draft' : getApprovedSaveStatus(statusValue);

      setPendingSaveAction(saveAction);

      try {
        const nextContentSnapshot = {
          additional_description: getValues('additional_description'),
          description: getValues('description'),
          facilities: getValues('facilities'),
          fine_print: getValues('fine_print'),
          transfer_details: serializeTransferDetails(getValues('transfer_details')),
          flight_destination_port: flightDestinationPortValue,
          flights_enabled: flightsEnabledValue,
          highlights: getValues('highlights'),
          location: resolveOfferLocationValue(locationValue.trim(), propertyName),
          name: headlineValue,
          offer_inclusions: getValues('offer_inclusions'),
          offer_tile_inclusion_heading: offerTileInclusionHeadingValue,
          preheader: emailPreheaderValue,
          sale_unit: saleUnitValue.trim(),
          slug: slugValue.trim(),
          status: nextStatus,
          subject_line: emailSubjectLineValue,
          type: typeValue,
        };

        await updateOffer(
          nextContentSnapshot.flight_destination_port
            ? nextContentSnapshot
            : { ...nextContentSnapshot, flight_destination_port: undefined },
        );

        setSavedContentSnapshot(nextContentSnapshot);
        setLocationValue(nextContentSnapshot.location);
        hasCopiedContentDraftRef.current = false;
        setStatusValue(nextStatus);
        onOfferStatusSaved?.(nextStatus);
        enqueueSnackbar(saveAction === 'approve' ? 'Offer content saved and approved' : 'Offer content saved', {
          variant: 'success',
        });
      } catch (error) {
        showSaveError(error, enqueueSnackbar);
      } finally {
        setPendingSaveAction(undefined);
      }
    },
    [
      emailPreheaderValue,
      emailSubjectLineValue,
      enqueueSnackbar,
      flightDestinationPortValue,
      flightsEnabledValue,
      getValues,
      headlineValue,
      locationValue,
      offerTileInclusionHeadingValue,
      onOfferStatusSaved,
      propertyName,
      saleUnitValue,
      setLocationValue,
      setStatusValue,
      slugValue,
      statusValue,
      typeValue,
      updateOffer,
    ],
  );
  const handleSaveDraft = useCallback(() => {
    void saveOfferContent('draft');
  }, [saveOfferContent]);
  const handleSaveAndApprove = useCallback(() => {
    void saveOfferContent('approve');
  }, [saveOfferContent]);
  const areAllSectionsExpanded =
    isGeneralSettingsExpanded &&
    isOfferPageExpanded &&
    isMerchandisingEmailExpanded &&
    isInclusionsExpanded &&
    isFlightsExpanded &&
    isSummaryBodyExpanded;

  useEffect(() => {
    if (skipCopiedContentDraftSync) {
      return;
    }

    setFlightsEnabledValue(!!offerFlightsEnabled);
  }, [offerFlightsEnabled, skipCopiedContentDraftSync]);

  useEffect(() => {
    if (hasCopiedContentDraftRef.current) {
      return;
    }

    setSavedContentSnapshot(offerContentSnapshot);
    reset({
      facilities: offerContentSnapshot.facilities,
      additional_description: offerContentSnapshot.additional_description,
      description: offerContentSnapshot.description,
      fine_print: offerContentSnapshot.fine_print,
      transfer_details: toTransferDetailsFormValue(offerContentSnapshot.transfer_details),
      highlights: offerContentSnapshot.highlights,
      offer_inclusions: offerContentSnapshot.offer_inclusions,
      offer_page_highlight_tags_override: getValues('offer_page_highlight_tags_override'),
    });
  }, [getValues, offerContentSnapshot, reset]);

  useEffect(() => {
    if (!copiedOfferContent) {
      return;
    }

    hasCopiedContentDraftRef.current = true;

    if (typeof copiedOfferContent.name === 'string') {
      setHeadlineValue(copiedOfferContent.name);
    }
    if (typeof copiedOfferContent.location === 'string') {
      setLocationValue(copiedOfferContent.location);
    }
    if (typeof copiedOfferContent.preheader === 'string') {
      setEmailPreheaderValue(copiedOfferContent.preheader);
    }
    if (typeof copiedOfferContent.subject_line === 'string') {
      setEmailSubjectLineValue(copiedOfferContent.subject_line);
    }
    if (typeof copiedOfferContent.flight_destination_port === 'string') {
      setFlightDestinationPortValue(copiedOfferContent.flight_destination_port);
    }
    if (typeof copiedOfferContent.flights_enabled === 'boolean') {
      setFlightsEnabledValue(copiedOfferContent.flights_enabled);
    }
    if (typeof copiedOfferContent.offer_tile_inclusion_heading === 'string') {
      setOfferTileInclusionHeadingValue(copiedOfferContent.offer_tile_inclusion_heading);
    }
    if (typeof copiedOfferContent.sale_unit === 'string') {
      setSaleUnitValue(copiedOfferContent.sale_unit);
    }
    if (typeof copiedOfferContent.type === 'string') {
      setTypeValue(copiedOfferContent.type);
    }

    reset({
      additional_description:
        typeof copiedOfferContent.additional_description === 'string'
          ? copiedOfferContent.additional_description
          : getValues('additional_description'),
      description:
        typeof copiedOfferContent.description === 'string' ? copiedOfferContent.description : getValues('description'),
      facilities:
        typeof copiedOfferContent.facilities === 'string' ? copiedOfferContent.facilities : getValues('facilities'),
      fine_print:
        typeof copiedOfferContent.fine_print === 'string' ? copiedOfferContent.fine_print : getValues('fine_print'),
      transfer_details:
        'transfer_details' in copiedOfferContent
          ? toTransferDetailsFormValue(copiedOfferContent.transfer_details)
          : getValues('transfer_details'),
      highlights:
        typeof copiedOfferContent.highlights === 'string' ? copiedOfferContent.highlights : getValues('highlights'),
      offer_inclusions: Array.isArray(copiedOfferContent.offer_inclusions)
        ? copiedOfferContent.offer_inclusions
        : getValues('offer_inclusions'),
      offer_page_highlight_tags_override: getValues('offer_page_highlight_tags_override'),
    });

    enqueueSnackbar('Copied offer content into the draft', { variant: 'success' });
  }, [
    copiedOfferContent,
    enqueueSnackbar,
    getValues,
    reset,
    setEmailPreheaderValue,
    setEmailSubjectLineValue,
    setFlightDestinationPortValue,
    setHeadlineValue,
    setLocationValue,
    setOfferTileInclusionHeadingValue,
    setSaleUnitValue,
    setTypeValue,
  ]);

  const handleToggleAllSections = () => {
    const nextExpandedState = !areAllSectionsExpanded;

    setIsGeneralSettingsExpanded(nextExpandedState);
    setIsOfferPageExpanded(nextExpandedState);
    setIsMerchandisingEmailExpanded(nextExpandedState);
    setIsInclusionsExpanded(nextExpandedState);
    setIsFlightsExpanded(nextExpandedState);
    setIsSummaryBodyExpanded(nextExpandedState);
  };

  const appendLuxPlusFinePrint = (content: string) => {
    const currentFinePrintValue = getValues('fine_print');

    setValue('fine_print', `${currentFinePrintValue}\n\n${content}`);
    setFinePrintMenuAnchorEl(null);
  };

  const openFinePrintMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFinePrintMenuAnchorEl(event.currentTarget);
  };

  const openTemplateModal = () => {
    setIsTemplateModalVisible(true);
  };

  const openGeneratedContentDialog = () => {
    setIsGeneratedContentDialogOpen(true);
  };

  const importOfferInclusions = async () => {
    if (!packages?.length) {
      enqueueSnackbar('No packages available to import inclusions from.', { variant: 'warning' });
      return;
    }

    const lowestPricePackage = packages.reduce((lowest, next) => (next.cost_price < lowest.cost_price ? next : lowest));

    try {
      const { result } = await InclusionsService.fetchPackageInclusions(offerId);
      const packageInclusions = result[lowestPricePackage.id_salesforce_external] ?? [];

      if (!packageInclusions.length) {
        enqueueSnackbar('No inclusions found on base package, nothing to import', { variant: 'warning' });
        return;
      }

      setValue(
        'offer_inclusions',
        packageInclusions.map((packageInclusion) => ({
          text: packageInclusion.inclusion?.name,
          is_highlighted: packageInclusion.style === 'highlighted',
          lux_plus_tier: packageInclusion.lux_plus_tier ?? null,
          icon: packageInclusion.inclusion?.category?.icon,
          category: packageInclusion.inclusion?.category?.name,
        })),
      );
    } catch (error) {
      enqueueSnackbar(`Failed to import inclusions: ${(error as Error).message}`, { variant: 'error' });
    }
  };

  const applyGeneratedContent = useCallback(
    (content: Partial<App.Offer>) => {
      const generatedFields = content as GeneratedContentFields;

      if (typeof generatedFields.description === 'string') {
        setValue('description', generatedFields.description);
      }

      enqueueSnackbar('Applied selected generated content', { variant: 'success' });
      setIsGeneratedContentDialogOpen(false);
    },
    [enqueueSnackbar, setValue],
  );

  return (
    <Box>
      <OfferTemplateModal
        show={isTemplateModalVisible}
        hide={() => setIsTemplateModalVisible(false)}
        templates={filteredTemplates}
        templateType={FINE_PRINT_TEMPLATE_TYPE}
        loadTemplate={(template) => {
          setValue('fine_print', template.content);
          setIsTemplateModalVisible(false);
        }}
      />
      <Dialog
        open={isGeneratedContentDialogOpen}
        onClose={() => setIsGeneratedContentDialogOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Generated {DESCRIPTION_CONTENT_TYPE} content</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                Generate, review, and apply suggested {DESCRIPTION_CONTENT_TYPE} copy from Accommodation Service.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AutoAwesomeIcon />}
                onClick={() => setIsGenerateContentModalVisible(true)}
                sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' }, whiteSpace: 'nowrap' }}
              >
                {hasVisibleGeneratedContent
                  ? `Regenerate ${DESCRIPTION_CONTENT_TYPE}`
                  : `Generate ${DESCRIPTION_CONTENT_TYPE}`}
              </Button>
            </Stack>
            {isGeneratedContentLoading && (
              <Stack alignItems="center" sx={{ py: 4 }}>
                <CircularProgress />
              </Stack>
            )}
            {!isGeneratedContentLoading && generatedContentErrorMessage && (
              <Alert severity="error">Failed to load generated content: {generatedContentErrorMessage}</Alert>
            )}
            {!isGeneratedContentLoading && !generatedContentErrorMessage && !hasVisibleGeneratedContent && (
              <Alert severity="info">No generated content yet.</Alert>
            )}
            {!isGeneratedContentLoading && !generatedContentErrorMessage && hasVisibleGeneratedContent && (
              <OfferGeneratedContentView content={visibleGeneratedContent} applyContent={applyGeneratedContent} />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setIsGeneratedContentDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {isGenerateContentModalVisible && (
        <GenerateContentModal
          onClose={() => setIsGenerateContentModalVisible(false)}
          initiateContentGeneration={initiateGeneratedContentGenerationMutation}
          fetchContent={refetchGeneratedContent}
          includeOfferInGeneration={hasAnyGeneratedContent}
          setUseWebSearch={setUseGeneratedContentWebSearch}
          useWebSearch={useGeneratedContentWebSearch}
          contentType={DESCRIPTION_CONTENT_TYPE}
        />
      )}

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4">Default content</Typography>
          <Typography variant="body1" color="text.secondary">
            Master copy used across the offer page, EDM, and merchandising surfaces.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button variant="outlined" size="small" startIcon={<UnfoldMoreIcon />} onClick={handleToggleAllSections}>
            {areAllSectionsExpanded ? 'Collapse all' : 'Expand all'}
          </Button>
          <OfferPagePreview offerId={offerId} offerSlug={slugValue} size="small" />
        </Stack>
      </Stack>

      <Accordion
        expanded={isGeneralSettingsExpanded}
        onChange={(_event, isExpanded) => setIsGeneralSettingsExpanded(isExpanded)}
        disableGutters
        variant="outlined"
        sx={ACCORDION_SX}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={ACCORDION_SUMMARY_SX}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <TuneIcon color="primary" />
            <Typography variant="h6">General settings</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={ACCORDION_DETAILS_SX}>
          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              maxWidth: 1280,
            }}
          >
            <FormControl fullWidth>
              <FormLabel sx={{ color: 'text.primary', fontWeight: 600, mb: 1 }}>Type</FormLabel>
              <Select
                displayEmpty
                value={typeValue}
                onChange={(event) => setTypeValue(event.target.value)}
                sx={{ bgcolor: 'background.paper' }}
              >
                {!typeValue && (
                  <MenuItem value="" disabled>
                    Loading type
                  </MenuItem>
                )}
                {typeOptions.map((type) => (
                  <MenuItem key={type} value={type}>
                    {(OFFER_FACING_NAMES as Record<string, string>)[type] ?? type}
                  </MenuItem>
                ))}
                {typeValue && !typeOptions.some((type) => type === typeValue) && (
                  <MenuItem value={typeValue}>
                    {(OFFER_FACING_NAMES as Record<string, string>)[typeValue] ?? typeValue}
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <FormLabel sx={{ color: 'text.primary', fontWeight: 600, mb: 1 }}>Status</FormLabel>
              <Select
                displayEmpty
                value={statusValue}
                onChange={(event) => setStatusValue(event.target.value)}
                sx={{ bgcolor: 'background.paper' }}
              >
                {!statusValue && (
                  <MenuItem value="" disabled>
                    Loading status
                  </MenuItem>
                )}
                {statusOptions.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
                {shouldShowStatusFallback && <MenuItem value={statusValue}>{statusValue}</MenuItem>}
              </Select>
              <FormHelperText>Visible to customers once Rates & Inventory is also approved.</FormHelperText>
            </FormControl>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={isOfferPageExpanded}
        onChange={(_event, isExpanded) => setIsOfferPageExpanded(isExpanded)}
        disableGutters
        variant="outlined"
        sx={{ ...ACCORDION_SX, mt: 3 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={ACCORDION_SUMMARY_SX}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <ArticleIcon color="primary" />
            <Typography variant="h6">Offer page</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={ACCORDION_DETAILS_SX}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              required
              label="Headline"
              value={headlineValue}
              onChange={(event) => setHeadlineValue(event.target.value)}
              helperText={`${headlineValue.length} characters · target 80-140`}
            />

            <AIGeneratorButton
              context={headlineAiContext}
              content={headlineValue}
              buttonLabel={headlineValue ? 'Rewrite Headline' : 'Generate Headline'}
              sx={AI_BUTTON_SX}
              updateContext={updateHeadlineAiContext}
              onChoose={setHeadlineValue}
            />

            <TextField
              fullWidth
              label="Location"
              value={locationValue}
              onChange={(event) => setLocationValue(event.target.value)}
              helperText="If left empty, the property name will be used when saved"
            />

            <TextField
              fullWidth
              label="URL slug"
              value={slugValue}
              onChange={(event) => setSlugValue(event.target.value)}
              error={isSlugInvalid}
              helperText={isSlugInvalid ? 'Slug must only contain lowercase letters, numbers, and hyphens' : ' '}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={isMerchandisingEmailExpanded}
        onChange={(_event, isExpanded) => setIsMerchandisingEmailExpanded(isExpanded)}
        disableGutters
        variant="outlined"
        sx={{ ...ACCORDION_SX, mt: 3 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={ACCORDION_SUMMARY_SX}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <EmailIcon color="primary" />
            <Typography variant="h6">Merchandising & email</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={ACCORDION_DETAILS_SX}>
          <Stack spacing={3}>
            <Box
              sx={{
                display: 'grid',
                gap: 3,
                gridTemplateColumns: { xs: '1fr', md: 'minmax(240px, 1fr) minmax(0, 2fr)' },
              }}
            >
              <TextField
                fullWidth
                label="Sale unit"
                value={saleUnitValue}
                helperText="Max 6 chars (room, boat, villa...)"
                slotProps={{ htmlInput: { maxLength: 6 } }}
                onChange={(event) => setSaleUnitValue(event.target.value)}
              />
              <TextField
                fullWidth
                label="Email subject line"
                value={emailSubjectLineValue}
                onChange={(event) => setEmailSubjectLineValue(event.target.value)}
              />
            </Box>

            <TextField
              fullWidth
              label="Email preheader"
              value={emailPreheaderValue}
              onChange={(event) => setEmailPreheaderValue(event.target.value)}
            />

            <Stack spacing={1}>
              <Box sx={{ '& .rdw-editor-main': { minHeight: 260 } }}>
                <RichTextEditor control={control} label="Description" name="description" />
              </Box>
              <Button
                variant="contained"
                startIcon={<AutoAwesomeIcon />}
                onClick={openGeneratedContentDialog}
                sx={{ alignSelf: 'flex-start' }}
              >
                Generate content
              </Button>
              <AIGeneratorButton
                context={descriptionAiContext}
                content={descriptionValue.trim() ? descriptionValue : undefined}
                buttonLabel={descriptionValue.trim() ? 'Rewrite description' : 'Generate description'}
                updateContext={updateDescriptionAiContext}
                onChoose={(value) => setValue('description', value)}
              />
            </Stack>

            <Stack spacing={1}>
              <Box sx={{ '& .rdw-editor-main': { minHeight: 180 } }}>
                <RichTextEditor
                  control={control}
                  label="Additional description"
                  name="additional_description"
                  helperText="Optional — appears below the main description on long-form surfaces."
                />
              </Box>
            </Stack>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={isInclusionsExpanded}
        onChange={(_event, isExpanded) => setIsInclusionsExpanded(isExpanded)}
        disableGutters
        variant="outlined"
        sx={{ ...ACCORDION_SX, mt: 3 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={ACCORDION_SUMMARY_SX}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} sx={{ width: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <CardGiftcardIcon color="primary" />
              <Typography variant="h6">Offer inclusions (marketing)</Typography>
            </Stack>
            <Chip
              size="small"
              label={`${visibleInclusionCount} ${visibleInclusionCount === 1 ? 'inclusion' : 'inclusions'}`}
            />
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={ACCORDION_DETAILS_SX}>
          <Stack spacing={3}>
            <FormControl fullWidth>
              <FormLabel sx={{ color: 'text.primary', fontWeight: 600, mb: 1 }}>Offer inclusion heading</FormLabel>
              <TextField
                fullWidth
                placeholder="Your handpicked inclusions:"
                value={offerTileInclusionHeadingValue}
                onChange={(event) => setOfferTileInclusionHeadingValue(event.target.value)}
              />
            </FormControl>
            <OfferInclusionsSelect
              actions={
                <Button
                  variant="text"
                  startIcon={<MenuBookIcon />}
                  href={vendorId ? `/vendors/${vendorId}/inclusions` : undefined}
                  target="_blank"
                  rel="noreferrer"
                  disabled={!vendorId}
                >
                  Inclusion catalogue
                </Button>
              }
              control={control}
              importOfferInclusions={importOfferInclusions}
              label="Offer inclusions"
              name="offer_inclusions"
              setValue={(name, value) => setValue(name as keyof SummaryBodyFormValues, value as never)}
              variant="offer-builder"
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={isSummaryBodyExpanded}
        onChange={(_event, isExpanded) => setIsSummaryBodyExpanded(isExpanded)}
        disableGutters
        variant="outlined"
        sx={{ ...ACCORDION_SX, mt: 3 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={ACCORDION_SUMMARY_SX}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <SubjectIcon color="primary" />
            <Typography variant="h6">Summary & body</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={ACCORDION_DETAILS_SX}>
          <Stack spacing={3}>
            <RichTextEditor control={control} label="Highlights" name="highlights" />

            <OfferPageHighlightTagsOverride control={control} offerId={offerId} />

            <Stack spacing={1.5}>
              <Box sx={{ '& .rdw-editor-main': { minHeight: 180 } }}>
                <RichTextEditor control={control} label="Fine print" name="fine_print" canUseBrandTokens />
              </Box>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', md: 'center' }}
                sx={{
                  '& .MuiButton-root': {
                    minHeight: 36,
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                <Button variant="outlined" startIcon={<BookmarksIcon />} onClick={openTemplateModal}>
                  Show templates
                </Button>
                <Button variant="outlined" startIcon={<FinePrintIcon />} onClick={openFinePrintMenu}>
                  Append Lux Plus Fine Print
                </Button>
                <Menu
                  anchorEl={finePrintMenuAnchorEl}
                  open={!!finePrintMenuAnchorEl}
                  onClose={() => setFinePrintMenuAnchorEl(null)}
                  anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                  <MenuItem onClick={() => appendLuxPlusFinePrint(LUX_PLUS_FINEPRINT_CONTENT_FLASH)}>
                    Limited Time Lux Exclusive
                  </MenuItem>
                  <MenuItem onClick={() => appendLuxPlusFinePrint(LUX_PLUS_FINEPRINT_CONTENT_LPC)}>
                    Lux Premium Collection
                  </MenuItem>
                </Menu>
                <Button
                  component={Link}
                  to={structuredFinePrintPath}
                  variant="outlined"
                  startIcon={<FinePrintIcon />}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Structured Fine Print
                </Button>
              </Stack>
            </Stack>

            <TransferDetailsFields control={control} />

            <Stack spacing={1.5}>
              <Box sx={{ '& .rdw-editor-main': { minHeight: 180 } }}>
                <RichTextEditor control={control} label="Facilities" name="facilities" />
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  disabled={isSyncingFacilities || !propertyId}
                  variant="outlined"
                  startIcon={<SyncIcon />}
                  onClick={() => propertyId && syncFacilities({ propertyId })}
                >
                  {isSyncingFacilities ? 'Syncing...' : 'Sync Facilities'}
                </Button>
                <Button
                  component={Link}
                  to={structuredFacilitiesPath}
                  variant="outlined"
                  startIcon={<TuneIcon />}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Structured Facilities
                </Button>
              </Stack>
              <Alert severity="info">
                Please fill in both Facilities and Structured Facilities so users on both sides of the A/B test can see
                the correct facilities experience, and so the legacy Facilities content remains available as a fallback.
              </Alert>
            </Stack>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={isFlightsExpanded}
        onChange={(_event, isExpanded) => setIsFlightsExpanded(isExpanded)}
        disableGutters
        variant="outlined"
        sx={{ ...ACCORDION_SX, mt: 3 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={ACCORDION_SUMMARY_SX}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <FlightTakeoffIcon color="primary" />
            <Typography variant="h6">Flights</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={ACCORDION_DETAILS_SX}>
          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: { xs: '1fr', md: '220px 280px' },
              maxWidth: 560,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={flightsEnabledValue}
                  onChange={(event) => setFlightsEnabledValue(event.target.checked)}
                />
              }
              label="Flights enabled"
              sx={{ alignSelf: 'center' }}
            />
            <FormControl fullWidth>
              <FormLabel sx={{ color: 'text.primary', fontWeight: 600, mb: 1 }}>Closest airport</FormLabel>
              <Select
                displayEmpty
                value={flightDestinationPortValue}
                onChange={(event) => setFlightDestinationPortValue(event.target.value)}
                sx={{ bgcolor: 'background.paper' }}
              >
                <MenuItem value="">
                  <Typography component="span" fontStyle="italic">
                    No closest airport selected
                  </Typography>
                </MenuItem>
                {flightDestinationPortOptions.map((airport) => (
                  <MenuItem key={airport} value={airport}>
                    {airport}
                  </MenuItem>
                ))}
                {flightDestinationPortValue &&
                  !flightDestinationPortOptions.some((airport) => airport === flightDestinationPortValue) && (
                    <MenuItem value={flightDestinationPortValue}>{flightDestinationPortValue}</MenuItem>
                  )}
              </Select>
            </FormControl>
          </Box>
        </AccordionDetails>
      </Accordion>

      <ContentActionBar
        hasUnsavedChanges={hasUnsavedChanges}
        isSaving={isSavingOffer}
        tabName="Default content"
        onDiscard={handleDiscard}
      >
        <Button
          variant="outlined"
          color="primary"
          disabled={isSavingOffer || isSlugInvalid}
          onClick={handleSaveDraft}
          sx={CONTENT_ACTION_BUTTON_SX}
        >
          {pendingSaveAction === 'draft' ? 'Saving...' : 'Save draft'}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={isSavingOffer || isSlugInvalid}
          startIcon={<CheckIcon />}
          onClick={handleSaveAndApprove}
          sx={CONTENT_ACTION_BUTTON_SX}
        >
          {pendingSaveAction === 'approve' ? 'Saving...' : 'Save & approve'}
        </Button>
      </ContentActionBar>
    </Box>
  );
}
